import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFieldMode } from '../context/FieldModeContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  SyncStatusBanner, QuickActionCard
} from './FieldModeComponents';
import {
  AlertTriangle, ClipboardList, FileText, Eye, Clock,
  Wifi, WifiOff, RefreshCw, ChevronRight, Home, Settings, LogOut,
  Shield, MapPin, Heart, ClipboardCheck
} from 'lucide-react';

const FieldModeDashboard = () => {
  const navigate = useNavigate();
  const { isOnline, syncStatus, forceSync, offlineDB, toggleFieldMode } = useFieldMode();
  const [recentItems, setRecentItems] = useState([]);
  const [pendingItems, setPendingItems] = useState([]);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const initData = async () => {
      try {
        // Load recent incidents from IndexedDB
        const incidents = await offlineDB.getAll('incidents');
        const ptps = await offlineDB.getAll('ptps');
        
        // Combine and sort by date
        const allItems = [
          ...incidents.map(i => ({ ...i, type: 'incident' })),
          ...ptps.map(p => ({ ...p, type: 'ptp' }))
        ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        
        setRecentItems(allItems.slice(0, 5));
        setPendingItems(allItems.filter(i => !i.synced));
      } catch (error) {
        console.error('Failed to load local data:', error);
      }
    };
    
    const initUser = () => {
      try {
        const userData = localStorage.getItem('user');
        if (userData) setUser(JSON.parse(userData));
      } catch (e) {
        console.error('Failed to load user data');
      }
    };
    
    initData();
    initUser();
  }, [offlineDB]);

  const loadLocalData = async () => {
    try {
      const incidents = await offlineDB.getAll('incidents');
      const ptps = await offlineDB.getAll('ptps');
      const allItems = [
        ...incidents.map(i => ({ ...i, type: 'incident' })),
        ...ptps.map(p => ({ ...p, type: 'ptp' }))
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setRecentItems(allItems.slice(0, 5));
      setPendingItems(allItems.filter(i => !i.synced));
    } catch (error) {
      console.error('Failed to load local data:', error);
    }
  };

  const quickActions = [
    {
      icon: AlertTriangle,
      title: 'Report Incident',
      description: 'Quick incident report',
      color: 'red',
      path: '/field/incident'
    },
    {
      icon: ClipboardList,
      title: 'Daily PTP',
      description: 'Pre-task planning',
      color: 'blue',
      path: '/field/ptp'
    },
    {
      icon: Eye,
      title: 'Near Miss',
      description: 'Report hazard',
      color: 'yellow',
      path: '/field/near-miss'
    },
    {
      icon: FileText,
      title: 'JHA',
      description: 'Job hazard analysis',
      color: 'green',
      path: '/field/jha'
    },
    {
      icon: Heart,
      title: 'First Aid',
      description: 'Injury report & aid',
      color: 'pink',
      path: '/injury-report'
    },
    {
      icon: ClipboardCheck,
      title: 'Observation',
      description: 'Safety observation',
      color: 'purple',
      path: '/observations'
    }
  ];

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-100" data-testid="field-mode-dashboard">
      {/* Sync Status Banner */}
      <SyncStatusBanner />
      
      {/* Header */}
      <div className={`bg-gradient-to-br from-blue-600 to-blue-800 text-white px-4 pt-${syncStatus.pendingCount || !isOnline ? '14' : '6'} pb-8`}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <Shield className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Field Mode</h1>
              <p className="text-blue-200 text-sm">
                {user?.name || 'Safety Worker'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={isOnline ? 'secondary' : 'warning'} className="gap-1">
              {isOnline ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
              {isOnline ? 'Online' : 'Offline'}
            </Badge>
            <Button 
              variant="ghost" 
              size="icon" 
              className="text-white hover:bg-white/20"
              onClick={() => navigate('/settings')}
            >
              <Settings className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{pendingItems.length}</p>
            <p className="text-xs text-blue-200">Pending Sync</p>
          </div>
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{recentItems.filter(i => i.type === 'incident').length}</p>
            <p className="text-xs text-blue-200">Incidents</p>
          </div>
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{recentItems.filter(i => i.type === 'ptp').length}</p>
            <p className="text-xs text-blue-200">PTPs</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="px-4 -mt-4">
        <div className="grid grid-cols-2 gap-3">
          {quickActions.map((action) => (
            <QuickActionCard
              key={action.title}
              icon={action.icon}
              title={action.title}
              description={action.description}
              color={action.color}
              onClick={() => navigate(action.path)}
            />
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="px-4 mt-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Recent Activity</h2>
          <Button variant="ghost" size="sm" onClick={loadLocalData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {recentItems.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No recent submissions</p>
              <p className="text-sm text-gray-400">Your reports will appear here</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {recentItems.map((item) => (
              <Card 
                key={item.id} 
                className={`cursor-pointer hover:shadow-md transition-shadow ${!item.synced ? 'border-l-4 border-l-yellow-400' : ''}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      item.type === 'incident' ? 'bg-red-100' : 'bg-blue-100'
                    }`}>
                      {item.type === 'incident' ? (
                        <AlertTriangle className="w-5 h-5 text-red-500" />
                      ) : (
                        <ClipboardList className="w-5 h-5 text-blue-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium truncate">
                          {item.type === 'incident' ? item.description?.slice(0, 40) || 'Incident Report' : item.title || 'Daily PTP'}
                        </p>
                        {!item.synced && (
                          <Badge variant="warning" className="text-xs">Pending</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(item.created_at).toLocaleDateString()}
                        </span>
                        {item.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {item.location}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Bottom Actions */}
      <div className="px-4 mt-6 pb-6">
        <Card>
          <CardContent className="p-4 space-y-3">
            <Button 
              variant="outline" 
              className="w-full justify-start gap-3"
              onClick={() => navigate('/dashboard')}
            >
              <Home className="w-5 h-5" />
              Exit Field Mode
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start gap-3"
              onClick={toggleFieldMode}
            >
              <Settings className="w-5 h-5" />
              Field Mode Settings
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start gap-3 text-red-600 hover:text-red-700"
              onClick={handleLogout}
            >
              <LogOut className="w-5 h-5" />
              Sign Out
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Floating Sync Button */}
      {!isOnline && syncStatus.pendingCount > 0 && (
        <div className="fixed bottom-6 right-6">
          <Button
            size="lg"
            className="rounded-full w-14 h-14 shadow-lg bg-yellow-500 hover:bg-yellow-600"
            onClick={forceSync}
            disabled={syncStatus.isSyncing}
          >
            {syncStatus.isSyncing ? (
              <RefreshCw className="w-6 h-6 animate-spin" />
            ) : (
              <span className="text-lg font-bold">{syncStatus.pendingCount}</span>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default FieldModeDashboard;
